import PromisePool from "es6-promise-pool";

import { CustomRequest, Payload, SendMessage } from "service-worker/models";

import {
  IHomePageInfo,
  ISynchronizationItem,
} from "utils/DexieDBManager/dexieDB.models";

import {
  getOfflineDocumentsCount,
  computePdfDocumentsToSave,
  reduceReceivedPdfDocuments,
  computePdfDocumentsToRemove,
  getIsAckRequiredDocumentsCount,
  getStarredDocumentVersionsCount,
  reduceSyncPdfDocumentsIdToRemove,
  getStarredIsAckRequiredDocumentsCount,
  getAcknowledgementDocumentVersionsCount,
} from "service-worker/helpers";

import {
  db,
  IPdfFile,
  IPdfFileMeta,
  putPdfDocument,
  putPdfDocumentMeta,
  deletePdfDocuments,
  addHomePageInfoToDB,
  deleteAllHomePageInfo,
  deletePdfDocumentsMeta,
  getAllPdfDocumentsMeta,
  addSynchronizationItemsToDB,
  deleteAllSynchronizationItems,
  addSynchronizationItemsMetaToDB,
  deleteAllSynchronizationItemsMeta,
} from "utils/DexieDBManager";

export const fetchPdfDocumentBinnary = async (pdfFileId: string, origin: string, fetchOptions: any) =>
{
  return await fetch(`${origin}/files/${pdfFileId}/binary`, fetchOptions);
}

export const fetchPdfThumbnailThumbnail = async (pdfFileId: string, origin: string, fetchOptions: any) =>
{
  return await fetch(`${origin}/files/${pdfFileId}/thumbnail`, fetchOptions);
}

export const updateHomePageInfoAsync = async (homePageInfo: IHomePageInfo) => {
  console.group('addHomePageInfoToDb');

  db.transaction('rw', db.homePageInfo, () => {
    deleteAllHomePageInfo().then(() => {
      addHomePageInfoToDB(homePageInfo);
    });
  });
};

export const updateSynchronizationItemsAsync = async ({ synchronizationItems, request }: Omit<Payload, 'haveToSynchronizeWithDB'>, sendMessage: SendMessage) => {
  console.group('update DB using SynchroVersion API');
 
  const pdfDocumentsToSave = reduceReceivedPdfDocuments(synchronizationItems);
  const pdfDocumentsIdToRemove = reduceSyncPdfDocumentsIdToRemove(synchronizationItems);
  await updatePdfDocuments(pdfDocumentsIdToRemove, pdfDocumentsToSave, request, sendMessage);
  console.groupEnd();
};

export const addOrUpdateSynchronizationItemsAsync = async ({
  synchronizationItems,
  haveToSynchronizeWithDB,
  request,
}: Payload,
sendMessageWithAddedSize: SendMessage
)=> {
  console.group('Update DB using versions API');
  
  db.transaction("rw", db.synchronizationItemsMeta, () => {
    deleteAllSynchronizationItemsMeta().then(() => {
      const synchronizationItemsMeta = synchronizationItems.map((item) => {

        const applicables = item.applicables;
        const nextApplicables = item.nextApplicables;
        const applicablesCount = applicables.length;
        const nextApplicablesCount = nextApplicables.length;
        const allDocuments = [...applicables, ...nextApplicables];
        return {
          id: item.id,
          name: item.name,
          categoryOrder: item.categoryOrder,
          offlineDocumentsVersionCount: getOfflineDocumentsCount(allDocuments),

          categoriesApplicableDocumentsCount: applicablesCount,
          categoriesNextApplicableDocumentsCount: nextApplicablesCount,
          categoriesDocumentVersionsCount: applicablesCount + nextApplicablesCount,
          categoriesIsAckRequiredApplicableDocumentsCount: getIsAckRequiredDocumentsCount(applicables),
          categoriesIsAckRequiredNextApplicableDocumentsCount: getIsAckRequiredDocumentsCount(nextApplicables),

          starredDocumentVersionsCount: getStarredDocumentVersionsCount(allDocuments),
          starredApplicableDocumentsCount: getStarredDocumentVersionsCount(applicables),
          starredNextApplicableDocumentsCount: getStarredDocumentVersionsCount(nextApplicables),
          starredIsAckRequiredApplicableDocumentsCount: getStarredIsAckRequiredDocumentsCount(applicables),
          starredIsAckRequiredNextApplicableDocumentsCount: getStarredIsAckRequiredDocumentsCount(nextApplicables),

          acknowledgementDocumentVersionsCount: getAcknowledgementDocumentVersionsCount(allDocuments),
          acknowledgementApplicableDocumentsCount: getAcknowledgementDocumentVersionsCount(applicables),
          acknowledgementNextApplicableDocumentsCount: getAcknowledgementDocumentVersionsCount(nextApplicables),
          acknowledgementIsAckRequiredApplicableDocumentsCount: getAcknowledgementDocumentVersionsCount(applicables),
          acknowledgementIsAckRequiredNextApplicableDocumentsCount: getAcknowledgementDocumentVersionsCount(nextApplicables),
        };
      });
      addSynchronizationItemsMetaToDB(synchronizationItemsMeta);
    });
  });
      
  db.transaction("rw", db.synchronizationItems, () => {
    deleteAllSynchronizationItems().then(() => {
      const synchronizationItemsCopy = synchronizationItems.map(
        (item) =>
          ({
            ...item,
            starredDocumentsCount: {
              applicables: getStarredDocumentVersionsCount(item.applicables),
              nextApplicables: getStarredDocumentVersionsCount(item.nextApplicables),
            },
          } as ISynchronizationItem)
      );
      addSynchronizationItemsToDB(synchronizationItemsCopy);
    });
  });

  if (haveToSynchronizeWithDB) {
    addOrUpdateVersionDocumentsAsync(synchronizationItems, request, sendMessageWithAddedSize);
  }
  console.groupEnd();
};

const addOrUpdateVersionDocumentsAsync = async (
  synchronizationItems: ISynchronizationItem[],
  request: CustomRequest,
  sendMessageWithAddedSize: SendMessage
  ) => {
    const existingPdfDocumentsMeta = await getAllPdfDocumentsMeta();
    
  const receivedPdfDocumentsFromRequest = reduceReceivedPdfDocuments(synchronizationItems);
  const pdfDocumentsIdToRemove = computePdfDocumentsToRemove(receivedPdfDocumentsFromRequest, existingPdfDocumentsMeta);

  const pdfDocumentsToSave = computePdfDocumentsToSave(receivedPdfDocumentsFromRequest, existingPdfDocumentsMeta);

  await updatePdfDocuments(pdfDocumentsIdToRemove, pdfDocumentsToSave, request, sendMessageWithAddedSize);
};

const updatePdfDocuments = async (
  pdfDocumentsToRemove: string[],
  pdfDocumentsToSave: IPdfFileMeta[],
  request: CustomRequest,
  sendMessageWithAddedSize: SendMessage
) => {
  db.transaction('rw!', db.documentPdfFiles, db.documentPdfFilesMeta, () => {
    deletePdfDocuments(pdfDocumentsToRemove);
    deletePdfDocumentsMeta(pdfDocumentsToRemove);
  });
  const { origin } = new URL(request.url);

  const fetchOptions = {
    headers: {
      Authorization: request.Authorization,
    },
  };

  let bufferSize = 0;
  const generatePromises: any = function*() {
    for (let count = 0; count < pdfDocumentsToSave.length; count++) {
      yield fetchPdfDocumentBinnary(pdfDocumentsToSave[count].id, origin, fetchOptions);
      yield fetchPdfThumbnailThumbnail(pdfDocumentsToSave[count].id, origin, fetchOptions);
    }
  }
  const promiseIterator = generatePromises();
  // Create a pool with 1 concurrent requests max, we care about low network (3G and 4G)
  const pool = new PromisePool(
    promiseIterator,
    2 // Configurable
  );

  pool.addEventListener('fulfilled', async function (event: any) {
    const requestResponse: Response = event.data.result;
    const [,, /* protocol */, /* empty */, fileId, fileType] = requestResponse.url.split('/');
    const pdfFileMeta = pdfDocumentsToSave.find((item) => item.id === fileId) as IPdfFileMeta;
    
    if (requestResponse.ok) {
        try {
            const fileBinary = await requestResponse.arrayBuffer();
            
            if (fileType === 'binary') {
                await updatePdfDocumentBuffer(pdfFileMeta, fileBinary);
            } else {
                await updatePdfDocumentThumbnailBuffer(pdfFileMeta, fileBinary);
            }
            
            const sizes = fileBinary.byteLength;
            bufferSize += sizes;
            sendMessageWithAddedSize(bufferSize);
        } catch (error) {
            console.error('Error processing request response:', error);
        }
    } else {
        console.error('Request failed with status:', requestResponse.status);
    }
});
  // Start the pool
  pool.start();
};

export const updatePdfDocumentBuffer = async (pdfFileMeta: IPdfFileMeta, pdfFileBinary: any) => {
    const pdfDocument: IPdfFile = { ...pdfFileMeta, pdfBuffer: pdfFileBinary };
    putPdfDocument(pdfDocument)
  };
  
export const updatePdfDocumentThumbnailBuffer = async (pdfFileMeta: IPdfFileMeta, pdfThumbnailBinary: ArrayBuffer) => {  
    const pdfDocumentMeta: IPdfFileMeta = {
        ...pdfFileMeta,
        isBufferSavedInDB: true,
        size: pdfFileMeta.size,
        thumbnailBuffer: pdfThumbnailBinary,
        thumbnailBufferSize: pdfThumbnailBinary.byteLength
    };
    putPdfDocumentMeta(pdfDocumentMeta)
  };
  